'use strict';

import { exists, generateUniqueNumber } from 'app_vodafone_ziggo/util/util';
import { validateForm } from 'app_vodafone_ziggo/util/validation';
import VZSpinner from 'Components/VZSpinner';

import { prefillQuestions } from './dataHandling';
import { sendGtmRouteChangeEvent, appendDataToName } from './gtm';
import { pushHistoryState } from './history';
import { insertResponse } from './responseHandling.js';
import { scrollTo } from './scrolling.js';

import { toggleBackButton, updateBackButtonTarget } from '../components/backbutton';
import { focusFirstInput, updateFieldWithAnswers } from '../components/form';
import { showModal } from '../components/modal';

import { getQuestionGroupAnswers } from '../steps/questions';
import { initStatements, generateStatementAnswers, statementBackButtonChecks, unbindKeyboardKeys } from '../steps/statements';
import { initAdvice } from '../steps/advice';
import { toggleQuitButton } from '../components/quitbutton';

let progressCounter = 0;

/**
 * @constant {Spinner} spinner - Spinner component;
 */
const spinner = document.querySelector('vz-spinner') || new VZSpinner();

/**
 * @private Will remove the pageTransition class
 */
function removePageTransitionClass() {
    Array.from(document.querySelectorAll(`${this.selectors.step}.${this.classes.pageTransition}`)).forEach((step) => {
        step.classList.remove(this.classes.pageTransition);
    });
}

/**
 * @public removeSubsequentSteps
 */
function removeSubsequentSteps() {
    let nextStep = this.currentStep.nextElementSibling;

    while (nextStep) {
        nextStep.parentNode.removeChild(nextStep);
        nextStep = this.currentStep.nextElementSibling;
    }
}

/**
 * @private getActiveScreen
 * @returns {HTMLElement} - The active statement or step
 */
function getActiveScreen() {
    const activeStep = document.querySelector(`${this.selectors.step}.${this.CONST.classes.active}`);
    const activeStatement = activeStep && activeStep.querySelector(`${this.selectors.statement}.${this.CONST.classes.active}`);
    return activeStatement || activeStep;
}

/**
 * @private animateStep
 */
function animateStep() {
    if (!this.currentStep || !this.targetStep) {
        console.error('fn animateStep: this.currentStep or this.targetStep missing');
        return;
    }

    // Add the `pageTransition` class to both steps to enable page transitioning
    // If the steps would have transitioning at all times, it won't not look very nice
    // when the screen is getting resized.
    this.targetStep.classList.remove(this.CONST.classes.active);
    this.targetStep.classList.add(this.classes.pageTransition);
    this.currentStep.classList.add(this.classes.pageTransition);

    if (!this.pageTransitionDuration) {
        this.pageTransitionDuration = parseFloat(getComputedStyle(this.currentStep).transitionDuration) * 1000;
    }

    scrollTo({ top: 0, left: 0 });

    // After the page transition is complete, remove the `pageTransition` this.classes.
    // This used to be done by listening to the `transitionEnd` event, but that was giving some buggy results sometimes
    setTimeout(() => {
        removePageTransitionClass.call(this);
    }, this.pageTransitionDuration);

    // If we add/remove the active class without a timeout of 0, the css transition is not handled well.
    setTimeout(() => {
        // Add `active` class to target step, then remove `active` class from current step.
        // This order is important for CSS page transitions.
        this.targetStep.classList.add(this.CONST.classes.active);
        this.currentStep.classList.remove(this.CONST.classes.active);

        this.currentStep = document.querySelector(`${this.selectors.step}.${this.CONST.classes.active}`);
    }, 0);

    // After transitioning to the new page, update the progress indicator
    if (this.targetStep.getAttribute(this.attributes.stepProgIndicator) === 'true') {
        if (this.currentStep.getAttribute(this.attributes.stepProgIndicator) === 'true') {
            progressCounter = this.goingBack ? progressCounter - 1 : progressCounter + 1;
        }
        this.progressIndicator.show();
        this.progressIndicator.activateNextStep(progressCounter);
    } else {
        this.progressIndicator.hide();
    }

    // If we transition from the intro page, add an extra class to this.targetStep for
    // a special intro animation
    if (this.currentStep.getAttribute(this.attributes.stepId) === this.CONST.bscanSteps.INTRO) {
        this.targetStep.classList.add(this.classes.introAnimation);

        const stepInput = this.targetStep.querySelector(this.CONST.selectors.form.input);
        if (exists(stepInput)) {
            const avatar = this.targetStep.querySelector(this.selectors.avatar);
            const animationDuration = parseFloat(getComputedStyle(avatar).animationDuration) * 1000;

            setTimeout(() => {
                // Only add focus if no navigation took place during animation.
                if (this.targetStep && this.targetStep.classList.contains(this.classes.introAnimation)) {
                    stepInput.focus();
                }
            }, animationDuration);
        }
    }
}

/**
 * @public transitionToStep
 */
function transitionToStep() {
    if (!this.currentStep || !this.targetStep || !this.targetStepIndex) {
        console.error('fn transitionToStep: this.currentStep, this.targetStep or this.targetStepIndex missing');
        return;
    }

    // Do nothing if this.targetStep is the same as this.currentStep
    if (this.currentStep === this.targetStep) {
        return;
    }

    this.targetStepId = this.targetStep.getAttribute(this.attributes.stepId);

    /**
     * @private initTransition
     */
    function initTransition() {
        // push custom event with step ID
        document.dispatchEvent(
            new CustomEvent(this.stepEvent, {
                detail: this.targetStep.getAttribute(this.attributes.stepId)
            })
        );

        [...this.targetStep.querySelectorAll('vz-carousel')].forEach((carousel) => {
            carousel.goToFirstSlide();
        });

        // Always move targetStep to the end of `.bscan-main` for advice pages,
        // so the direction of the step transition is always from the right
        if (!this.goingBack && this.currentStep.classList.contains(this.classes.advicePages)) {
            const lastStep = document.querySelector(`${this.selectors.mainContainer} ${this.selectors.step}:last-child`);
            lastStep.parentNode.insertBefore(this.targetStep, lastStep.nextSibling);
        }

        this.targetStep.classList.remove(this.classes.skipped);

        animateStep.call(this, this.currentStep, this.targetStep);

        // If it's not the first step, show the back button in the header
        setTimeout(
            () => {
                toggleBackButton.call(this);
                toggleQuitButton.call(this);
            },
            this.goingBack ? 0 : this.pageTransitionDuration
        );

        // Push the history change for the new step
        if (!this.historyChange) {
            pushHistoryState.call(this); // eslint-disable-line no-use-before-define
        } else {
            // Push this.targetStep into `stepsOrder` if there was a history change
            // This will create a correct order necessary for steps after the `Advice` screen
            this.stepsOrder.push(`${this.targetStepIndex}__${generateUniqueNumber()}`);
        }

        updateBackButtonTarget.call(this); // eslint-disable-line no-use-before-define
        updatePageTitle.call(this); // eslint-disable-line no-use-before-define

        // Add the current step id to the main container for css purposes
        this.mainContainer.setAttribute(this.attributes.currentStep, this.targetStepId);

        // Auto select the first input for UX convenience sake,
        // which also will auto popup the onscreen keyboard for devices that support that
        setTimeout(() => {
            focusFirstInput.call(this);
        }, this.pageTransitionDuration);

        // Statements step
        if (this.targetStep.classList.contains(this.classes.statements.container)) {
            initStatements.call(this);
            // Questions step
        } else if (this.targetStep.classList.contains(this.classes.stepQuestion)) {
            unbindKeyboardKeys.call(this);
            // Advice step
        } else if (this.targetStep.classList.contains(this.classes.adviceAnimation)) {
            initAdvice.call(this);
        }
    }

    // If a transition delay has been defined, transition with a timeout
    // Once the timeout is done, check to see if the user has not changed step/statement
    if (this.transitionDelay > 0) {
        const currActiveScreen = getActiveScreen.call(this);

        setTimeout(() => {
            const newActiveScreen = getActiveScreen.call(this);

            // If the screen has changed, do nothing
            if (newActiveScreen === currActiveScreen) {
                initTransition.call(this);
            }
        }, this.transitionDelay);
    } else {
        initTransition.call(this);
    }
}

/**
 * @public submitAnswer
 * @param {HTMLElement} clickedButton - the clicked button
 * @description Submit the data and retrieve the next step
 */
function submitAnswer(clickedButton) {
    // Answer has changed. Remove subsequent steps as they might be affected.
    if (this.currentStepAnswer && this.currentStepId in this.givenAnswers && this.givenAnswers[this.currentStepId] !== this.currentStepAnswer) {
        removeSubsequentSteps.call(this);
    }

    if (this.currentStep.classList.contains(this.classes.stepQuestionGroup)) {
        this.currentStepAnswer.forEach((questionAnswer) => {
            this.givenAnswers[questionAnswer.id] = questionAnswer.answer || null;
        });
    } else {
        this.givenAnswers[this.currentStepId] = this.currentStepAnswer || null;
    }

    updateFieldWithAnswers.call(this, clickedButton);

    const endpointQuestion = this.mainContainer && this.mainContainer.getAttribute(this.attributes.endpointQuestion);
    const csrfToken = window.csrf.token;
    const csrfName = window.csrf.tokenName;

    const formData = new FormData();
    formData.append('index', this.targetStepIndex);
    formData.append(csrfName, csrfToken);
    formData.append('isGroupOfQuestions', this.groupedQuestions.length > 0);

    if (this.currentStepAnswer && this.currentStepAnswer !== this.values.skipped) {
        formData.append('questionid', this.currentStepId);
        formData.append('answer', typeof this.currentStepAnswer === 'object' ? JSON.stringify(this.currentStepAnswer) : this.currentStepAnswer);
    }

    if (this.currentStepAnswer === this.values.skipped) {
        formData.append('questionid', this.currentStepId);
        formData.append('answer', '');
    }

    spinner.start();
    fetch(endpointQuestion, {
        method: 'POST',
        body: formData
    })
        .then((res) => res.text())
        .then((response) => {
            this.targetStep = document.querySelector(`[${this.attributes.stepIndex}="${this.targetStepIndex}"]`);

            // If the target step already exists, just transition to it
            if (this.targetStep) {
                transitionToStep.call(this);
                return;
            }

            // On success, insert the step's HTML into the dom
            insertResponse.call(this, response);

            // Check to see if this.targetStep does indeed exist in the new HTML
            this.targetStep = document.querySelector(`[${this.attributes.stepIndex}="${this.targetStepIndex}"]`);

            if (!this.targetStep) {
                console.error('fn goToStep: this.targetStep does not exist');

                if (JSON.parse(response).action === 'CSRF-AjaxFail') {
                    showModal('ajax-fail-modal', 'Can\'t find "ajax fail" modal');
                } else {
                    showModal('try-again-modal', 'Can\'t find "try again" modal');
                }

                return;
            }

            // Transition to the new step
            transitionToStep.call(this);
            prefillQuestions.call(this);
        })
        .catch((response) => {
            document.dispatchEvent(
                new CustomEvent(this.CONST.events.responseError, {
                    detail: response
                })
            );
        })
        .finally(spinner.stop);
}

/**
 * @public goToStep
 * @param {Event} event - The click/tap event
 * @param {string} targetStepIndex - (optional) The step index to go to
 */
function goToStep(event, targetStepIndex) {
    event.preventDefault();
    let clickedButton;
    this.currentStep = document.querySelector(`${this.selectors.step}.${this.CONST.classes.active}`);

    // If there is a stepIndex in the history state, transition to it
    // Please note that any changes done in the current step will not be sent, before transitioning to the other step
    if (event.state && event.state.stepIndex) {
        const trimmedStepIndex = event.state.stepIndex.split('__')[0];
        this.targetStep = document.querySelector(`[${this.attributes.stepIndex}="${trimmedStepIndex}"]`);
        this.targetStepIndex = this.targetStep.getAttribute(this.attributes.stepIndex);
        this.transitionDelay = 0;

        if (this.targetStep) {
            transitionToStep.call(this);
        } else {
            // If `this.targetStep` cannot be found, it's likely that this is an old browser history state from before the user has refreshed the page.
            // In that case, nuke the page by forcing a reload, which should remove the old history state due to the empty pushState in `addHistoryListeners`
            window.location = window.location.href;
        }

        return;
    }

    this.historyChange = false;

    // If we're dealing with a form submit, bind the form's button to `clickedButton`
    if (event.type === this.CONST.events.submit) {
        clickedButton = event.target.querySelector(this.selectors.button.next);
        event.preventDefault();
    } else if (event.target) {
        // Else it must be a click on the button itself
        clickedButton = event.target;
    }

    if (!clickedButton) {
        console.error('fn goToStep: event submitter unknown');
        return;
    }

    // Set all necessary variables
    this.currentStepId = this.currentStep.getAttribute(this.attributes.stepId);
    this.currentStepName = this.currentStep.getAttribute(this.attributes.stepName);
    this.currentStepInput = this.currentStep.querySelector(this.CONST.selectors.form.input);
    this.targetStepIndex = targetStepIndex || clickedButton.getAttribute(this.attributes.targetStep);
    this.targetStep = document.querySelector(`[${this.attributes.stepIndex}="${this.targetStepIndex}"]`);
    this.goingBack = clickedButton.classList.contains(this.selectors.button.back.substring(1));
    this.transitionDelay = (!this.goingBack && clickedButton.getAttribute(this.attributes.delay) * 1000) || 0;
    this.groupedQuestions = this.currentStep.querySelectorAll(this.selectors.groupedQuestions);

    // If we're using the back button from the header, just reuse the browser's back functionality
    if (!event.state && this.goingBack) {
        window.history.back();
        return;
    }

    // If we're going back to the advice, set `this.goingBack` to true so the step transition animation goes from right to left
    if (clickedButton.classList.contains(this.selectors.button.backToAdvice.substring(1))) {
        this.goingBack = true;
    }

    // Release focus of form elements, so it's not possible to submit the form multiple times by pressing enter multiple times
    const validForm = validateForm(this.currentStep);
    clickedButton.blur();

    if (this.currentStepInput && validForm) {
        this.currentStepInput.blur();
    }

    this.currentStepAnswer = null;

    // when selecting a company from the list
    if (clickedButton.getAttribute(this.attributes.stepAnswer)) {
        this.currentStepAnswer = clickedButton.getAttribute(this.attributes.stepAnswer);
        // when having multiple questions in one step
    } else if (this.groupedQuestions.length > 0) {
        this.currentStepAnswer = getQuestionGroupAnswers.call(this);
        // when a value is entered in an input field
    } else if (this.currentStepInput && this.currentStepInput.value) {
        this.currentStepAnswer = this.currentStepInput.value;
        // if the questions are skipped
    } else if (this.currentStep.classList.contains(this.classes.stepQuestion) || this.currentStep.classList.contains(this.classes.stepQuestionGroup)) {
        this.currentStepAnswer = this.values.skipped;
        // is part of statements
    } else if (this.currentStep.classList.contains(this.classes.statements.container)) {
        this.currentStepAnswer = generateStatementAnswers.call(this);
    }

    const answerDidNotChange = this.currentStepId in this.givenAnswers && this.givenAnswers[this.currentStepId] === this.currentStepAnswer;
    if (this.goingBack || (answerDidNotChange && this.targetStep)) {
        // If we're going back or we're resubmitting an existing question while the answer has not changed, just transition to the step

        transitionToStep.call(this);
        return;
    }

    if (
        !this.targetStepIndex || // Stop if `targetStepIndex` is undefined
        (this.goingBack && statementBackButtonChecks.call(this)) || // Stop if we're going back and `statementBackButtonChecks` returns true
        this.currentStep.classList.contains(this.classes.pageTransition) // Stop if a page transition is already active
    ) {
        return;
    }

    submitAnswer.call(this, clickedButton);
}

/**
 * @public skipStep
 * @param {Event} event - The click/tap event
 */
function skipStep(event) {
    this.currentStepInput = document.querySelector(`${this.selectors.step}.${this.CONST.classes.active} ${this.CONST.selectors.form.input}`);
    this.currentStepInput.removeAttribute('required');
    this.currentStepInput.value = '';

    goToStep.call(this, event);
}

/**
 * @public pdLinkHandler
 * @param {Event} event - The click/tap event
 */
function pdLinkHandler(event) {
    const link = event.target;
    const linkTarget = link.getAttribute(this.CONST.attributes.href);

    if (!linkTarget || linkTarget.indexOf('bs:') !== 0) {
        return;
    }

    event.preventDefault();
    const targetStepValue = linkTarget.split(':')[1];

    if (!this.currentStep) {
        this.currentStep = this.currentStep || document.querySelector(`${this.selectors.step}.${this.CONST.classes.active}`);
    }

    const currentStepIndex = parseInt(this.currentStep.getAttribute(this.attributes.stepIndex), 10);

    let targetStepIndex;
    if (targetStepValue === 'next') {
        targetStepIndex = currentStepIndex + 1;
    } else if (targetStepValue === 'prev') {
        targetStepIndex = currentStepIndex - 1;
    } else {
        targetStepIndex = targetStepValue;
    }

    goToStep.call(this, event, targetStepIndex.toString());
}

/**
 * @public updatePageTitle
 * @param {string} stepName - (Optional) The step's name
 */
function updatePageTitle(stepName) {
    if (stepName) {
        document.title = `${this.baseTitle} - ${stepName}`;
        return;
    }

    this.targetStepName = this.targetStep.getAttribute(this.attributes.stepName);
    this.targetStepId = this.targetStep.getAttribute(this.attributes.stepId);

    // If there is a target step name and we're not on the statements step
    if (this.targetStepName && !this.targetStep.classList.contains(this.classes.statements.container)) {
        this.targetStepName = this.targetStepName.replace(/\s/g, '-');

        this.targetStepName = appendDataToName(this.targetStepId, this.targetStepName, this.currentStepName);

        document.title = `${this.baseTitle} - ${this.targetStepName}`;
        sendGtmRouteChangeEvent.call(this, this.targetStepName);
    }
}

export { goToStep, pdLinkHandler, removeSubsequentSteps, skipStep, submitAnswer, transitionToStep, updatePageTitle };
